'use client'

import OfferLinkArrow from './offer-link-arrow'
import OfferMessage from './offer-message'

import OfferUsd from './offer-usd'

import { useState } from 'react'
import Link from 'next/link'
import { Bitcoin, ChevronDown, ChevronUp } from 'lucide-react'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { offerUsd } from '@/utils/offer-currency'
import type { Offer } from '@/utils/offers'
import { offerLevels, type OfferBookFilter, type OfferLevel } from '@/utils/offer-book'
import styles from './collection-offer-book.module.css'

export const bookBtc = (sats: number) =>
	(sats / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })
export type OfferSelection = {
	scope: 'collection' | 'trait'
	price: number
	trait?: { type: string; value: string }
}

function Expiry({ offer, now }: { offer: Offer; now: number }) {
	const date = new Date(offer.expires_at)
	const minutes = Math.max(1, Math.ceil((date.getTime() - now) / 60000))
	const left =
		minutes >= 1440
			? `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h`
			: minutes >= 60
			? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
			: `${minutes}m`
	return (
		<time dateTime={date.toISOString()} title={date.toLocaleString()}>
			Expires in {left}
		</time>
	)
}

export default function CollectionOfferBook({
	offers,
	wallet,
	now,
	onOffer,
	full = false
}: {
	offers: Offer[]
	wallet: string | null
	now: number
	onOffer: (selection: OfferSelection) => void
	full?: boolean
}) {
	const { exchangeRate } = useExchangeRate()
	const [filter, setFilter] = useState<OfferBookFilter>('collection')
	const [grouped, setGrouped] = useState(true)
	const [expanded, setExpanded] = useState<string | null>(null)
	const levels = offerLevels(offers, filter, wallet, full ? grouped : true)
	const maxQuantity = Math.max(1, ...levels.map((l) => l.offers.length))
	const counts = {
		collection: offers.filter((o) => o.scope === 'collection').length,
		trait: offers.filter((o) => o.scope === 'trait').length,
		item: offers.filter((o) => o.scope === 'item').length,
		mine: wallet ? offers.filter((o) => o.buyer_address === wallet).length : 0
	}
	const select = (level: OfferLevel) => {
		const offer = level.offers[0]
		onOffer({
			scope: offer.scope === 'trait' ? 'trait' : 'collection',
			price: offer.price_sats,
			...(offer.scope === 'trait' && offer.trait_type && offer.trait_value
				? { trait: { type: offer.trait_type, value: offer.trait_value } }
				: {})
		})
	}
	return (
		<div className={styles.book}>
			<div className={styles.tabs} role="group" aria-label="Filter offers">
				{(['collection', 'trait', 'item', 'mine'] as const).map((key) => (
					<button
						key={key}
						type="button"
						aria-pressed={filter === key}
						onClick={() => {
							setFilter(key)
							setExpanded(null)
						}}
					>
						{{ collection: 'Collection', trait: 'Traits', item: 'Items', mine: 'My offers' }[key]}
						<span>{counts[key]}</span>
					</button>
				))}
			</div>
			{full && <div className={styles.toolbar}>
				<span>Highest price first</span>
				<label style={{ display: 'inline-flex', gap: 5, margin: 0 }}>
					<input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />
					Group prices
				</label>
			</div>}
			{levels.length ? (
				<div className={full ? styles.fullLevels : styles.levels}>
					<div className={styles.columns} aria-hidden="true">
						<span>Price (BTC)</span>
						<span>Quantity</span>
						<span>Total (BTC)</span>
					</div>
					{levels.map((level) => (
						<div key={level.key} className={styles.level}>
							<button
								type="button"
								className={styles.levelButton}
								aria-expanded={expanded === level.key}
								aria-label={`${bookBtc(level.price)} BTC${
									offerUsd(level.price, exchangeRate)
										? ` (${offerUsd(level.price, exchangeRate)})`
										: ''
								}, ${level.offers.length} ${level.label} offers. Show details`}
								onClick={() => setExpanded(expanded === level.key ? null : level.key)}
							>
								<span className={styles.price}>
									<Bitcoin size={12} aria-hidden="true" />
									{bookBtc(level.price)}
									<OfferUsd sats={level.price} block />
								</span>
								<span className={styles.quantity}>
									<span
										aria-hidden="true"
										className={styles.depth}
										style={{ width: `${(level.offers.length / maxQuantity) * 100}%` }}
									/>
									<span>{level.offers.length}</span>
								</span>
								<span className={styles.total}>
									<span>{bookBtc(level.total)}</span>
									<OfferUsd sats={level.total} block />
									{expanded === level.key ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
								</span>
								{level.scope !== 'collection' && (
									<span className={styles.eligibility}>{level.label}</span>
								)}
							</button>
							{expanded === level.key && (
								<div className={styles.details}>
									{level.offers.map((offer) => (
										<div className={styles.buyer} key={offer.id}>
											<div>
												<span title={offer.buyer_address}>
													{offer.buyer_address === wallet ? (
														<strong>Your offer</strong>
													) : (
														`${offer.buyer_address.slice(0, 8)}…${offer.buyer_address.slice(-5)}`
													)}
												</span>
												<Expiry offer={offer} now={now} />
												<OfferMessage offer={offer} message={offer.message} />
											</div>
											{offer.buyer_address === wallet ? (
												<Link href="/wallet#offers-sent">
													Manage offer <OfferLinkArrow />
												</Link>
											) : offer.scope === 'item' && offer.inscription_id ? (
												<Link href={`/inscription/${offer.inscription_id}#item-offers`}>
													View item <OfferLinkArrow />
												</Link>
											) : null}
										</div>
									))}
									{level.scope !== 'item' && (
										<button className={styles.match} type="button" onClick={() => select(level)}>
											Make {level.scope === 'trait' ? 'trait' : 'collection'} offer at this price
										</button>
									)}
								</div>
							)}
						</div>
					))}
				</div>
			) : (
				<div className={styles.empty}>
					<strong>
						{filter === 'mine'
							? wallet
								? 'No active offers from you'
								: 'Your offers, in one place'
							: `No ${filter === 'trait' ? 'trait' : filter} offers yet`}
					</strong>
					<p>
						{filter === 'mine'
							? wallet
								? 'Choose a price and make your first offer.'
								: 'Connect your wallet when you make an offer to track it here.'
							: 'Set your price. Sellers can accept when they’re ready.'}
					</p>
				</div>
			)}
		</div>
	)
}
